import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import { ThemeContextProvider } from './theme/ThemeContext.tsx'
import { featureFlags } from './utils/featureFlags'

/**
 * Resolve the API base URL the same way `preAuthApi.ts` does:
 * window.APP_CONFIG.API_URL (deployed, injected by CDK into `config.js`),
 * then VITE_API_URL (local dev), then nothing (skip the fetch entirely).
 */
function resolveApiUrl(): string | null {
  const windowWithConfig = window as { APP_CONFIG?: { API_URL?: string } }
  return (
    windowWithConfig.APP_CONFIG?.API_URL ||
    import.meta.env.VITE_API_URL ||
    null
  )
}

async function bootstrap(): Promise<void> {
  // Hydrate runtime feature flags from the backend BEFORE React renders so
  // that components calling `featureFlags.isEnabled(...)` in their render
  // path see the current values. `loadFromApi` is contractually non-throwing
  // (it logs and falls back to build-time defaults on any failure), but we
  // wrap in try/catch here as defense-in-depth so a boot never blocks on it.
  const apiUrl = resolveApiUrl()
  if (apiUrl) {
    try {
      await featureFlags.loadFromApi(apiUrl)
    } catch (error) {
      console.warn('[bootstrap] unexpected error loading feature flags', error)
    }
  }

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ThemeContextProvider>
        <App />
      </ThemeContextProvider>
    </StrictMode>,
  )
}

void bootstrap()
